// @ts-check

// Vendor imports
const _ = require('lodash')
const React = require('react')

// Default option values
const DEFAULT_SCROLL_THROTTLE_FREQUENCY = 100 // ms
const DEFAULT_LOAD_MORE_THRESHOLD = 40 // px

/**
 * @summary
 * React Hook that monitors the scroll state of an element
 * and fires a callback when scrolled near the bottom.
 * @param {Object} options
 * @param {boolean} options.disabled
 * @param {function} options.onLoadMore
 * Required callback that is called when scroll is within
 * the `options.loadMoreThreshold` from the bottom.
 * @param {number} [options.loadMoreThreshold] (in pixels)
 * @param {number} [options.scrollThrottleFrequency] (in milliseconds)
 * @returns {React.RefObject<HTMLElement>}
 */
function useInfiniteScroll(options) {
  const scrollableRef = React.useRef(null)

  // Do nothing during server-side rendering
  if (typeof window === 'undefined') {
    return scrollableRef
  }

  const {
    loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD,
    scrollThrottleFrequency = DEFAULT_SCROLL_THROTTLE_FREQUENCY,
    onLoadMore
  } = options

  React.useEffect(() => {
    if (options.disabled) {
      return
    }
    const scrollableEl = scrollableRef.current

    const onScroll = _.throttle(() => {
      const scrollViewportBottomY = scrollableEl.scrollTop + scrollableEl.clientHeight
      const loadMoreY = scrollableEl.scrollHeight - loadMoreThreshold
      if (scrollViewportBottomY >= loadMoreY) {
        onLoadMore()
      }
    }, scrollThrottleFrequency)

    scrollableEl.addEventListener('scroll', onScroll)

    return () => {
      scrollableEl.removeEventListener('scroll', onScroll)
      onScroll.cancel()
    }
  })

  return scrollableRef
}

module.exports = useInfiniteScroll
